"use client";

import React, { useEffect } from "react";

import clsx from "clsx";
import styled, { css } from "styled-components";

import { rem } from "polished";
import { useMountTransition } from "../../hooks/use-mount-transition";
import Portal from "../../hooks/use-portal";

export const Drawer = ({
  wrapperId,
  isOpen,
  children,
  showClose = false,
  position = "right",
  hasOverlay = false,
  removeWhenClosed = true,
  width = 400,
  fullWidth = false,
  styles,
  parentStyle,
  className,
  zIndex = 2,
  scrollable = false,
  disableOverlayClick = false,
  portalPlacement = "body",
  isFixed = true,
  animate = true,
  locked = true,
  handleClose = () => {},
}) => {
  const isTransitioning = useMountTransition({
    isMounted: isOpen,
    unmountDelay: 300,
  });

  if (!isTransitioning && removeWhenClosed && !isOpen) {
    return null;
  }

  return (
    <Portal
      wrapperId={wrapperId}
      portalPlacement={portalPlacement}
      locked={locked}
    >
      <StyleDiv
        zIndex={zIndex}
        scrollable={scrollable}
        aria-hidden={isOpen ? "false" : "true"}
        isFixed={isFixed}
        animate={animate}
        style={parentStyle}
        className={clsx("drawer-provider", className, {
          open: isOpen,
          in: animate && isTransitioning,
        })}
      >
        <div
          className={clsx("drawer", position)}
          role="dialog"
          style={
            styles
              ? styles
              : { maxWidth: !fullWidth ? `${width}px` : "calc(100% - 50px)" }
          }
        >
          {showClose && (
            <div className="close" onClick={handleClose}>
              {/* <IconPlus size={18} style={{ transform: "rotate(45deg)" }} /> */}
              +
            </div>
          )}

          <div className="drawer-content">{children}</div>
        </div>
        {hasOverlay ? (
          <div
            className="backdrop"
            onClick={disableOverlayClick ? () => null : handleClose}
          />
        ) : null}
      </StyleDiv>
    </Portal>
  );
};
export default Drawer;

const StyleDiv = styled.div`
  ${({ theme, zIndex, scrollable, isFixed, animate }) => css`
    &.drawer-provider {
      transition: 0.3s ease all;
      height: 100%;
      &.open {
        transition: 0.3s;
        &.in {
          transition: 0.3s;
          .left,
          .right {
            transform: translateX(0);
          }

          .drawer {
            &.left {
              .close {
                transform: translateX(50px);
                box-shadow: 5px 0 5px -2px ${theme.colors.black};
              }
            }

            &.right {
              .close {
                transform: translateX(-50px);
                box-shadow: -5px 0 -5px 2px ${theme.colors.black};
              }
            }
          }

          .backdrop {
            visibility: visible;
            opacity: 1;
            pointer-events: auto;
            z-index: 4;
          }
        }
      }

      .backdrop {
        visibility: hidden;
        opacity: 0;
        /* background: rgba(0, 0, 0, 0.5); */
        transition: opacity 0.3s ease, visibility 0.3s ease;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        position: fixed;
        pointer-events: none;
        z-index: 0;

        ${!isFixed &&
        css`
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        `}
      }

      .drawer {
        background: ${theme.colors.white};
        height: 100%;
        width: 100%;
        position: ${isFixed ? "fixed" : "relative"};
        transition: transform 0.3s ease;
        z-index: ${zIndex ? zIndex : 2};

        .drawer-content {
          box-shadow: 0 3px 6px ${theme.colors.black};
          height: 100%;
          overflow: ${scrollable ? "hidden auto" : "none"};
        }

        .close {
          position: absolute;
          top: 0;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #ccc;
          color: #fff;
          cursor: pointer;
          transform: translateX(0px);
          transition: transform 0.3s ease;
        }

        &.right {
          top: 0;
          right: 0;
          transform: ${animate ? "translateX(100%)" : "translateX(0%)"};

          .close {
            left: 0;
          }
        }

        &.left {
          top: 0;
          left: 0;
          transform: ${animate ? "translateX(-105%)" : "translateX(0%)"};

          .close {
            right: 0;
          }
        }
      }

      &.timelinePortal {
        .drawer-content {
          box-shadow: none;
        }
      }
    }
  `}
`;

export const StyledDrawerContent = styled.div`
  ${({ theme, isMobile }) => css`
    display: flex;
    flex-direction: column;
    height: 100%;

    .drawer-header,
    .drawer-body,
    .drawer-footer {
      padding: ${rem(15)} ${isMobile ? rem(25) : rem(50)};
    }

    .drawer-header {
      background-color: ${theme.color.text};
      padding-top: ${rem(31)};
      padding-bottom: ${rem(31)};
      color: ${theme.color.body};
      display: flex;
      align-items: center;
      justify-content: space-between;

      .drawer-close {
        position: relative;
        transform: rotate(45deg);
        cursor: pointer;
      }
    }

    .drawer-body {
      flex-grow: 1;
      overflow: hidden auto;
      scroll-behavior: smooth;
      padding-top: ${rem(30)};

      .content {
        p {
          font-size: ${isMobile ? rem(12) : rem(15)};

          + p {
            margin-top: ${rem(20)};
          }
        }
      }
    }

    .drawer-footer {
      .wrapper {
        padding-top: 30px;
      }
    }
  `}
`;
